
import { useState } from 'react';

export default function RecruitForm() {
  const [formData, setFormData] = useState({
    position: '',
    name: '',
    age: '',
    phone: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.position || !formData.name || !formData.phone || !formData.email) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('position', formData.position);
      formBody.append('name', formData.name);
      formBody.append('age', formData.age);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6dd0iatehdqnvnpis40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          position: '',
          name: '',
          age: '',
          phone: '',
          email: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="recruit-form" className="py-12 sm:py-20 px-4 sm:px-6 bg-gray-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            応募フォーム
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 tracking-widest uppercase">ENTRY</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm p-5 sm:p-8 space-y-4 sm:space-y-6"
          data-readdy-form
          id="recruit-application-form"
        >
          <div>
            <label htmlFor="position" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              希望職種 <span className="text-red-500">※</span>
            </label>
            <select
              id="position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="">お選びください</option>
              <option value="営業職">営業職</option>
              <option value="現場職人">現場職人</option>
              <option value="どちらでも可">どちらでも可</option>
            </select>
          </div>

          <div>
            <label htmlFor="recruit-name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              お名前 <span className="text-red-500">※</span>
            </label>
            <input
              type="text"
              id="recruit-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="山田 太郎"
            />
          </div>

          <div>
            <label htmlFor="age" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              年齢
            </label>
            <input
              type="text"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="例：30"
            />
          </div>

          <div>
            <label htmlFor="recruit-phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              お電話番号 <span className="text-red-500">※</span>
            </label>
            <input
              type="tel"
              id="recruit-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="090-0000-0000"
            />
          </div>

          <div>
            <label htmlFor="recruit-email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              メールアドレス <span className="text-red-500">※</span>
            </label>
            <input
              type="email"
              id="recruit-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="example@email.com"
            />
          </div>

          <div>
            <label htmlFor="recruit-message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              自己PR・ご質問など
            </label>
            <textarea
              id="recruit-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              maxLength={500}
              rows={5}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              placeholder="経験やスキル、ご質問などをご記入ください"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
          </div>

          {submitStatus === 'success' && (
            <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-md text-green-800 text-xs sm:text-sm">
              ご応募ありがとうございます。担当者より折り返しご連絡いたします。
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-xs sm:text-sm">
              送信に失敗しました。必須項目をご確認の上、もう一度お試しください。
            </div>
          )}

          <div className="text-center pt-2 sm:pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-10 sm:px-12 py-3 sm:py-4 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? '送信中...' : '応募する'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
